import React, { useEffect, useMemo, useRef } from "react";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";

/**
 * MiniNetwork — reusable network/flow visualisation.
 *
 * Renders a "host + peers" SVG graph driven by a list of events that have
 * `src_ip`/`dest_ip` fields. Edges fade in as `activeItems` grows; the
 * "current" pair (from `focusEvent`) is highlighted with animated request /
 * response markers.
 *
 * Props
 * ─────
 * allItems:     [{src_ip, dest_ip, …}]   — full event set (defines graph topology)
 * activeItems:  [{src_ip, dest_ip, …}]   — events already "played" (drives edge activation)
 * hostLabel:    string                    — preferred root IP (falls back to first seen)
 * focusEvent:   {src_ip, dest_ip, status, timestamp, playTs?}  — current event for highlighting
 * isPlaying:    boolean
 * showProgress: boolean                   — when false, only the host is visible
 * accent:       CSS color (focused-node stroke)
 * playbackSpeed:number                    — affects request/response motion duration
 * maxPeers:     number (default 30)
 */

// ── helpers ──────────────────────────────────────────────────────────────────
const safeStr = (v) => (v == null ? "" : String(v));

const formatTickSeconds = (ts) => {
  if (!ts) return "-";
  const d = dayjs(ts);
  return d.isValid() ? d.format("DD/MM HH:mm:ss") : "-";
};

function buildMiniNetwork(allItems, activeItems, hostLabel, focusEvent, maxPeers = 30) {
  const requestedHost = safeStr(hostLabel) || "";
  const focusSrc = safeStr(focusEvent?.src_ip) || "-";
  const focusDst = safeStr(focusEvent?.dest_ip) || "-";

  const firstSeenIndex = new Map();
  (allItems || []).forEach((it, idx) => {
    const src = safeStr(it?.src_ip);
    const dst = safeStr(it?.dest_ip);
    if (src && src !== "-" && !firstSeenIndex.has(src)) firstSeenIndex.set(src, idx);
    if (dst && dst !== "-" && !firstSeenIndex.has(dst)) firstSeenIndex.set(dst, idx);
  });

  const peersAll = Array.from(firstSeenIndex.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([ip]) => ip);

  const rootLabel =
    (requestedHost && firstSeenIndex.has(requestedHost) && requestedHost) ||
    peersAll[0] ||
    requestedHost ||
    "Host";

  const peersExcludingRoot = peersAll.filter((ip) => ip !== rootLabel);
  const peersToShow = peersExcludingRoot.slice(0, maxPeers);
  const overflowCount = Math.max(0, peersExcludingRoot.length - peersToShow.length);
  const overflowPeers = overflowCount > 0 ? peersExcludingRoot.slice(maxPeers) : [];

  const toNodeId = (label) => `peer-${safeStr(label).replace(/[^a-zA-Z0-9._-]/g, "_")}`;

  const CANVAS_W = 1200;
  const cols = Math.min(10, Math.max(1, peersToShow.length));
  const rows = Math.max(1, Math.ceil(peersToShow.length / cols));
  const rowGap = 64;
  const svgHeight = 160 + Math.max(0, rows - 1) * rowGap;
  const hostY = svgHeight / 2;

  const nodes = [{ id: "host", label: rootLabel, x: 70, y: hostY, role: "host" }];

  const peerAreaMinX = 260;
  const peerAreaMaxX = CANVAS_W - 140;
  const colStep = cols > 1 ? (peerAreaMaxX - peerAreaMinX) / (cols - 1) : 0;
  const rowYs = Array.from({ length: rows }, (_, r) => {
    const startY = hostY - ((rows - 1) * rowGap) / 2;
    return startY + r * rowGap;
  });

  peersToShow.forEach((label, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = peerAreaMinX + col * colStep;
    const y = rowYs[row] ?? hostY;
    nodes.push({ id: toNodeId(label), label, x, y, role: "peer" });
  });

  if (overflowCount > 0) {
    nodes.push({ id: "peer-more", label: `+${overflowCount} more`, x: peerAreaMaxX + 60, y: hostY, role: "more" });
  }

  const labelToNodeId = new Map();
  labelToNodeId.set(rootLabel, "host");
  nodes.forEach((n) => { if (n.role === "peer") labelToNodeId.set(n.label, n.id); });

  const toNodeOrMore = (ip) => {
    const s = safeStr(ip);
    if (!s || s === "-") return null;
    const direct = labelToNodeId.get(s);
    if (direct) return direct;
    if (overflowCount > 0 && overflowPeers.includes(s)) return "peer-more";
    return null;
  };

  const currentFrom = toNodeOrMore(focusEvent?.src_ip);
  const currentTo = toNodeOrMore(focusEvent?.dest_ip);
  const currentPairKey =
    currentFrom && currentTo && currentFrom !== currentTo
      ? [currentFrom, currentTo].sort().join("<->")
      : null;

  const edgePairsAll = new Map();
  const addPair = (from, to) => {
    if (!from || !to || from === to) return;
    const pairKey = [from, to].sort().join("<->");
    if (!edgePairsAll.has(pairKey)) {
      const [aId, bId] = pairKey.split("<->");
      edgePairsAll.set(pairKey, { aId, bId, pairKey });
    }
  };
  (allItems || []).forEach((it) => addPair(toNodeOrMore(it?.src_ip), toNodeOrMore(it?.dest_ip)));
  const edges = Array.from(edgePairsAll.values());

  const activeDirected = new Set();
  const activePairs = new Set();
  const responsePairs = new Set();
  const activeNodes = new Set(["host"]);
  (activeItems || []).forEach((it) => {
    const from = toNodeOrMore(it?.src_ip);
    const to = toNodeOrMore(it?.dest_ip);
    if (!from || !to || from === to) return;
    const dirKey = `${from}->${to}`;
    const pairKey = [from, to].sort().join("<->");
    activeDirected.add(dirKey);
    activePairs.add(pairKey);
    activeNodes.add(from);
    activeNodes.add(to);
    const status = safeStr(it?.status).toLowerCase();
    if (status.includes("response")) responsePairs.add(pairKey);
  });
  activePairs.forEach((pairKey) => {
    const [x, y] = pairKey.split("<->");
    if (activeDirected.has(`${x}->${y}`) && activeDirected.has(`${y}->${x}`)) responsePairs.add(pairKey);
  });

  return {
    nodes, edges,
    focusSrc, focusDst,
    activeNodes, activePairs, responsePairs,
    svgHeight, hostY, canvasW: CANVAS_W,
    currentPairKey, currentFrom, currentTo,
  };
}

// ── component ────────────────────────────────────────────────────────────────
const MiniNetworkImpl = ({
  allItems = [],
  activeItems = [],
  hostLabel,
  focusEvent,
  isPlaying = false,
  showProgress = false,
  accent,
  playbackSpeed = 1,
  maxPeers = 30,
  title = "Mini network",
}) => {
  const theme = useTheme();
  const net = useMemo(
    () => buildMiniNetwork(allItems, activeItems, hostLabel, focusEvent, maxPeers),
    [allItems, activeItems, hostLabel, focusEvent, maxPeers]
  );

  const prevActiveEdgesRef = useRef(new Set());
  const prevActiveNodesRef = useRef(new Set());

  useEffect(() => {
    if (!isPlaying) {
      prevActiveEdgesRef.current = new Set();
      prevActiveNodesRef.current = new Set(["host"]);
      return;
    }
    prevActiveEdgesRef.current = new Set(net.activeEdges || []);
    prevActiveNodesRef.current = new Set(net.activeNodes || ["host"]);
  }, [isPlaying, net.activeEdges, net.activeNodes]);

  useEffect(() => {
    if (!isPlaying) return;
    prevActiveEdgesRef.current = new Set(net.activeEdges || []);
    prevActiveNodesRef.current = new Set(net.activeNodes || ["host"]);
  }, [isPlaying, net.activeEdges, net.activeNodes]);

  const nodeFill = (role) => {
    if (role === "host") return theme.palette.primary.main;
    if (role === "more") return theme.palette.grey[800];
    return theme.palette.grey[700];
  };

  const isFocusedNode = (label) => label && (label === net.focusSrc || label === net.focusDst);

  const headerTs = useMemo(() => {
    const ts = focusEvent?.playTs ?? focusEvent?.timestamp;
    return formatTickSeconds(ts);
  }, [focusEvent?.playTs, focusEvent?.timestamp]);

  const motionDur = useMemo(() => {
    const s = Number(playbackSpeed) || 1;
    const clamped = Math.max(0.25, Math.min(2, s));
    return (0.9 / clamped).toFixed(2);
  }, [playbackSpeed]);

  const focusAccent = accent || theme.palette.primary.main;
  const requestColor = theme.palette.info?.light || "#00E5FF";
  const responseColor = theme.palette.success?.light || "#7CFF6B";

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%", height: "100%",
        display: "flex", flexDirection: "column",
        overflow: "hidden", borderRadius: 2,
        backgroundColor: "background.default",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{headerTs}</Typography>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, flex: 1, minHeight: 0, display: "flex" }}>
        <svg
          width="100%" height="100%"
          viewBox={`0 0 ${net.canvasW ?? 1200} ${net.svgHeight ?? 180}`}
          role="img" aria-label="mini network view"
          style={{ display: "block", flex: 1, minHeight: 0 }}
        >
          {/* edges */}
          {net.edges.map((e, idx) => {
            const a = net.nodes.find((n) => n.id === e.aId);
            const b = net.nodes.find((n) => n.id === e.bId);
            if (!a || !b) return null;

            const pairKey = e.pairKey;
            const isActive = showProgress ? net.activePairs?.has(pairKey) : false;
            const wasActive = prevActiveEdgesRef.current.has(pairKey);
            const justActivated = showProgress && isActive && !wasActive;
            const hasResponse = Boolean(net.responsePairs?.has(pairKey));
            const isCurrent = Boolean(net.currentPairKey && pairKey === net.currentPairKey);

            const stroke = isCurrent ? requestColor : isActive ? requestColor : "rgba(163,163,163,0.18)";
            const strokeWidth = isCurrent ? 4.5 : isActive ? 2 : 1.25;

            const leftNode = a.x <= b.x ? a : b;
            const rightNode = a.x <= b.x ? b : a;
            const dx = rightNode.x - leftNode.x;
            const dy = rightNode.y - leftNode.y;
            const baseLen = Math.hypot(dx, dy) || 1;
            const hash = Array.from(pairKey).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
            const sign = hash % 2 === 0 ? 1 : -1;
            const fan = Math.max(18, Math.min(140, Math.abs(dy) * 1.1 + baseLen * 0.06));
            const c1x = leftNode.x + dx * 0.35;
            const c2x = leftNode.x + dx * 0.72;

            const c1yReq = leftNode.y + sign * fan;
            const c2yReq = rightNode.y + sign * (fan * 0.55);
            const dUpperL2R = `M ${leftNode.x} ${leftNode.y} C ${c1x} ${c1yReq} ${c2x} ${c2yReq} ${rightNode.x} ${rightNode.y}`;
            const dUpperR2L = `M ${rightNode.x} ${rightNode.y} C ${c2x} ${c2yReq} ${c1x} ${c1yReq} ${leftNode.x} ${leftNode.y}`;

            const c1yRes = leftNode.y - sign * fan;
            const c2yRes = rightNode.y - sign * (fan * 0.55);
            const dLowerL2R = `M ${leftNode.x} ${leftNode.y} C ${c1x} ${c1yRes} ${c2x} ${c2yRes} ${rightNode.x} ${rightNode.y}`;
            const dLowerR2L = `M ${rightNode.x} ${rightNode.y} C ${c2x} ${c2yRes} ${c1x} ${c1yRes} ${leftNode.x} ${leftNode.y}`;

            const currentDirIsL2R = (() => {
              if (!isCurrent || !net.currentFrom || !net.currentTo) return true;
              if (net.currentFrom === leftNode.id && net.currentTo === rightNode.id) return true;
              if (net.currentFrom === rightNode.id && net.currentTo === leftNode.id) return false;
              const srcNode = net.nodes.find((n) => n.id === net.currentFrom);
              const dstNode = net.nodes.find((n) => n.id === net.currentTo);
              if (srcNode && dstNode) return srcNode.x <= dstNode.x;
              return true;
            })();

            const requestPath = currentDirIsL2R ? dUpperL2R : dUpperR2L;
            const responsePath = currentDirIsL2R ? dLowerR2L : dLowerL2R;

            return (
              <g key={`${pairKey}-${idx}`}>
                <path
                  d={dUpperL2R} stroke={stroke} strokeWidth={strokeWidth}
                  strokeLinecap="round" fill="none"
                  opacity={isActive ? 1 : 0}
                  pathLength={100} strokeDasharray={100}
                  strokeDashoffset={isActive ? 0 : 100}
                />
                {justActivated ? (
                  <>
                    <animate attributeName="opacity" from="0" to="1" dur="0.25s" fill="freeze" />
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="0.55s" fill="freeze" />
                  </>
                ) : null}

                {showProgress && isActive && isCurrent ? (
                  <path d={requestPath} stroke={requestColor} strokeWidth={6.5} strokeLinecap="round" fill="none" opacity={0.9} strokeDasharray="0" />
                ) : null}

                {isActive && isCurrent ? (
                  <path d={requestPath} stroke={requestColor} strokeWidth={12} strokeLinecap="round" fill="none" opacity={0.18} style={{ filter: "blur(1.2px)" }} />
                ) : null}

                {showProgress && isActive && isCurrent && hasResponse ? (
                  <path d={responsePath} stroke={responseColor} strokeWidth={3.75} strokeLinecap="round" fill="none" opacity={0.85} />
                ) : null}

                {showProgress && isActive && isCurrent ? (
                  <>
                    <circle r="4.4" fill={requestColor} opacity="0.98">
                      <animateMotion dur={`${motionDur}s`} repeatCount="indefinite" path={requestPath} rotate="auto" />
                    </circle>
                    {hasResponse ? (
                      <>
                        <circle r="4.4" fill={responseColor} opacity="0.98">
                          <animateMotion dur={`${motionDur}s`} repeatCount="indefinite" path={responsePath} rotate="auto" />
                        </circle>
                        <circle cx={leftNode.x} cy={leftNode.y} r="0" fill={responseColor} opacity="0">
                          <animate attributeName="r" values="0;0;11;0" dur={`${motionDur}s`} repeatCount="indefinite" keyTimes="0;0.55;0.7;1" />
                          <animate attributeName="opacity" values="0;0;0.28;0" dur={`${motionDur}s`} repeatCount="indefinite" keyTimes="0;0.55;0.7;1" />
                        </circle>
                      </>
                    ) : null}
                  </>
                ) : null}
              </g>
            );
          })}

          {/* nodes */}
          {net.nodes.map((n) => {
            const focused = isFocusedNode(n.label);
            const r = n.role === "host" ? 18 : focused ? 14 : 12;
            const stroke = focused ? focusAccent : "rgba(255,255,255,0.9)";
            const sw = focused ? 3 : 2;
            const isNodeActive = showProgress ? net.activeNodes?.has(n.id) : n.id === "host";
            const nodeOpacity = isNodeActive ? 1 : 0;
            const wasNodeActive = prevActiveNodesRef.current.has(n.id);
            const nodeJustActivated = isPlaying && isNodeActive && !wasNodeActive && n.id !== "host";

            return (
              <g key={n.id}>
                <circle cx={n.x} cy={n.y} r={r} fill={nodeFill(n.role)} stroke={stroke} strokeWidth={sw} opacity={nodeOpacity} />
                {nodeJustActivated ? (
                  <>
                    <animate attributeName="opacity" from="0" to="1" dur="0.25s" fill="freeze" />
                    <animateTransform attributeName="transform" type="scale" additive="sum" from="0.85" to="1" dur="0.25s" fill="freeze" />
                  </>
                ) : null}
                <text x={n.x} y={n.y + r + 14} textAnchor="middle" fontSize="11" fill={theme.palette.text.secondary} style={{ fontWeight: 700 }} opacity={nodeOpacity}>
                  {n.label.length > 18 ? `${n.label.slice(0, 16)}…` : n.label}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Paper>
  );
};

const MiniNetwork = React.memo(MiniNetworkImpl, (prev, next) => {
  if (prev.allItems !== next.allItems) return false;
  if (prev.activeItems !== next.activeItems) return false;
  if (prev.hostLabel !== next.hostLabel) return false;
  if (prev.isPlaying !== next.isPlaying) return false;
  if (prev.showProgress !== next.showProgress) return false;
  if (prev.accent !== next.accent) return false;
  if (prev.playbackSpeed !== next.playbackSpeed) return false;
  if (prev.maxPeers !== next.maxPeers) return false;
  if (prev.title !== next.title) return false;
  const a = prev.focusEvent || {}, b = next.focusEvent || {};
  if (a.id !== b.id) return false;
  if (a.playTs !== b.playTs) return false;
  if (a.timestamp !== b.timestamp) return false;
  return true;
});

export default MiniNetwork;
